/** A standard menu containing a user's gallections generally used as the
 *  target of some action like where to move a deviation or other folder */
import React from 'react';
import { compose } from 'redux';
import classnames from 'classnames';
import { connectMediaStore } from '../../_hocs/withOwnReduxStore';
import { convertToFoldersWithSubfolders } from '../../../utils/folders';
import withCurrentGallection, {
  WithCurrentGallectionProps,
} from '../../_hocs/withCurrentGallection';
import withCurrentSubfolder, {
  WithCurrentSubfolderProps,
} from '../../_hocs/withCurrentSubfolder';
import withGallections, {
  WithGallectionsProps,
} from '../../_hocs/withGallections';
import {
  getCanEditGallection,
  isSpecialFullyLockedFolder,
} from '../../helpers';

import { getAllGallections } from '../../selectors';
import { PapiGallection } from '@wix/da-papi-types';
import StandardMenu from '@wix/da-ds/pkg/Dropdown/MenuPreset/StandardMenu';
import {
  NullableDropdownItem,
  NullableDropdownGroup,
} from '@wix/da-ds/pkg/Dropdown/types';
import { isPremiumGallection } from '../../../utils/gallections';
import TextTooltipOnHover from '../../../Popover/Preset/TextTooltipOnHover';
import PremiumIcon from '../../../DeviationViews/Helper/PremiumIcon';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';

import gallectionListMenuStyles from './GallectionListMenu.scss';

export interface Props {
  className?: string;
  firstItem?: NullableDropdownItem;
  // display only folders that have no parent i.e. not a subfolder
  onlyTopLevelFolders?: boolean;
  // a list of folder ids not to display in the menu
  excludeFolderIds?: (number | null)[];
  disableFolderIds?: (number | null)[];
  disableReasonHint?: string;
  onSelect: (folderId: number) => void;
}

const enhance = compose(
  withCurrentGallection,
  withCurrentSubfolder,
  withGallections,
  connectMediaStore(state => ({
    allGallections: getAllGallections(state),
  })) as any
);

const GallectionListMenu: React.FC<
  Props &
    WithCurrentGallectionProps &
    WithCurrentSubfolderProps &
    WithGallectionsProps & { allGallections: PapiGallection[] }
> = ({
  className,
  onSelect,
  allGallections,
  onlyTopLevelFolders,
  disableFolderIds = [],
  disableReasonHint,
  firstItem,
  currentFolderId,
  currentSubfolderId,
  excludeFolderIds = [],
}) => {
  /** is a given folder shown in this gallection menu */
  const isFolderShown = (folder: PapiGallection): boolean => {
    const currentGallectionId = currentSubfolderId || currentFolderId;
    return (
      // a folder itself is not a target of it's own actions
      folder.folderId !== currentGallectionId &&
      // don't target a non-manageable folder
      !isSpecialFullyLockedFolder(folder) &&
      // given disabled folders aren't shown
      !excludeFolderIds.includes(folder.folderId)
    );
  };

  /** build an array of dropdown items to be used in the final menu */
  const buildDropdownItemsFromFolders = (): NullableDropdownItem[] => {
    const menuItems: NullableDropdownItem[] = [];
    const foldersWithSubfolders =
      convertToFoldersWithSubfolders(allGallections);
    foldersWithSubfolders.forEach(folder => {
      const parentFolderShown = isFolderShown(folder);
      let icon: any;
      const label: React.ReactNode = folder.name;
      if (isPremiumGallection(folder)) {
        icon = (
          <PremiumIcon
            premiumFolderData={folder.premiumFolderData}
            withTooltip={false}
            size={IconSize.SMALLER}
          />
        );
      }
      const disabled =
        !getCanEditGallection(folder) ||
        disableFolderIds.includes(folder.folderId);

      const parentDropdownItem = {
        value: folder.folderId,
        disabled,
        icon,
        label:
          disabled && disableReasonHint ? (
            <TextTooltipOnHover
              placement="right"
              tooltipText={disableReasonHint}
              renderReference={({ ref }) => (
                <span style={{ pointerEvents: 'all' }} ref={ref}>
                  {label}
                </span>
              )}
            />
          ) : (
            label
          ),
        ...(!parentFolderShown && { disabled: true }),
      };

      const subfolderItems = onlyTopLevelFolders
        ? []
        : folder.subfolders?.filter(isFolderShown).map(subfolder => ({
            value: subfolder.folderId,
            disabled:
              !getCanEditGallection(subfolder) ||
              disableFolderIds.includes(subfolder.folderId),
            label: subfolder.name,
            className: gallectionListMenuStyles['subfolder'],
          })) ?? [];

      // note: even if the parent folder shouldn't be shown, it's subfolders might be
      // in that case, show the parent folder as disabled
      if (subfolderItems.length || parentFolderShown) {
        menuItems.push(parentDropdownItem);
      }
      menuItems.push(...subfolderItems);
    });
    if (firstItem) {
      return [
        { items: [firstItem] },
        { items: menuItems },
      ] as NullableDropdownGroup[];
    }
    return menuItems;
  };

  return (
    <StandardMenu
      id="gallection-list-menu"
      lightText
      className={classnames(gallectionListMenuStyles['root'], className)}
      items={buildDropdownItemsFromFolders()}
      onSelect={onSelect}
    />
  );
};
GallectionListMenu.displayName = 'GallectionListMenu';

export default enhance(GallectionListMenu) as React.ComponentClass<Props>;
